import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Lock, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordReset = () => {
  const [step, setStep] = useState("request"); // request | confirm | done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        toast.success("Reset code sent to your email!");
        setStep("confirm");
      } else {
        toast.error("Failed to send reset email");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      if (response.ok) {
        toast.success("Password reset successfully!");
        setStep("done");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <PublicNavbar />

      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <main className="relative z-10 pt-28 pb-20 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FAFAFA] mb-2">Reset Password</h1>
            <p className="text-[#A1A1AA]">
              {step === "request" && "Enter your email to receive a reset code"}
              {step === "confirm" && "Enter the code from your email"}
              {step === "done" && "Your password has been reset"}
            </p>
          </div>

          <div className="bg-[#121214] border border-[#27272A] rounded-xl p-8">
            {step === "request" && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA]">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B]"
                >
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </form>
            )}

            {step === "confirm" && (
              <form onSubmit={handleConfirmReset} className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA]">Reset Code</Label>
                  <Input
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    required
                    className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] text-center text-lg tracking-widest"
                    placeholder="XXXXXXXX"
                    maxLength={8}
                  />
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B]"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep("request")}
                  className="w-full text-sm text-[#71717A] hover:text-[#D4AF37]"
                >
                  Didn&apos;t receive a code? Try again
                </button>
              </form>
            )}

            {step === "done" && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-[#22C55E]" />
                </div>
                <p className="text-[#A1A1AA] mb-6">
                  You can now sign in with your new password.
                </p>
                <div className="flex flex-col gap-3">
                  <Link to="/staff/login">
                    <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B]">
                      Staff Login
                    </Button>
                  </Link>
                  <Link to="/client/login">
                    <Button variant="outline" className="w-full border-[#27272A] text-[#FAFAFA]">
                      Client Login
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {step !== "done" && (
              <p className="mt-6 text-center text-sm text-[#A1A1AA]">
                <Link to="/staff/login" className="text-[#D4AF37] hover:underline flex items-center justify-center gap-1">
                  <ArrowLeft size={14} />
                  Back to login
                </Link>
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PasswordReset;
